import React from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Info, Search, X } from 'lucide-react';

import { DisclosureChevron } from './DisclosureChevron';
import { ListContinuation } from './ListContinuation';
import { RowAction } from './RowAction';
import { isVisibilityManaged } from '../hooks/useColumnVisibility';
import { StatusBadge } from './StatusBadge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Skeleton } from './ui/skeleton';
import { formatNumber } from '../lib/formatters';
import { prefersReducedMotion } from '../lib/motion';
import { cn, FOCUS_RING_ITEM } from '../lib/utils';
import type { InfiniteList, ListRemoval, ListStatus, RowMark } from '../hooks/useInfiniteList';

/* ------------------------------------------------------------------ */
/* SearchInput — the one search-box treatment (debouncing is caller's) */
/* ------------------------------------------------------------------ */

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  /**
   * Plural glossary noun of what is searched — the list's `itemNoun`
   * ('sensors', 'event types', 'app clients'). Required: it is what the
   * accessible name says is being searched.
   */
  noun: string;
  /**
   * 'Search by {up to three fields a person would type}…' — the only place a
   * list names its searched fields. Defaults to 'Search by name…'.
   */
  placeholder?: string;
  /**
   * `/` focuses this box from anywhere on the page (ignored while typing in
   * another field). Full-page list searches pass it; dialog and picker
   * searches do not.
   */
  shortcut?: boolean;
  /**
   * Fill the container instead of the kit's toolbar width. Only for a search
   * above a list inside a dialog or picker; a list page never sets a width.
   */
  fill?: boolean;
}

const isTypingTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
};

/**
 * "Search {noun}", plus the placeholder's field hint when it follows the
 * 'Search by …' pattern: noun 'lenses' + 'Search by name, alias, or template…'
 * → 'Search lenses by name, alias, or template'.
 */
export function searchAccessibleName(noun: string, placeholder: string): string {
  const match = /^Search (by .+?)(?:…|\.\.\.)?$/.exec(placeholder.trim());
  return match ? `Search ${noun} ${match[1]}` : `Search ${noun}`;
}

export const SearchInput: React.FC<SearchInputProps> = ({
  value,
  onChange,
  noun,
  placeholder = 'Search by name…',
  shortcut = false,
  fill = false,
}) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (!shortcut) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;
      event.preventDefault();
      inputRef.current?.focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [shortcut]);

  return (
    <div className={cn('relative w-full', !fill && 'max-w-xs')} data-list-search="">
      <Search
        aria-hidden="true"
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
      />
      <Input
        ref={inputRef}
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={searchAccessibleName(noun, placeholder)}
        aria-keyshortcuts={shortcut ? '/' : undefined}
        className="pl-9 pr-8 [&::-webkit-search-cancel-button]:hidden"
      />
      {value ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          className="absolute right-0.5 top-1/2 -translate-y-1/2"
          onClick={() => onChange('')}
          aria-label="Clear search"
        >
          <X aria-hidden="true" />
        </Button>
      ) : null}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* DataTable                                                          */
/* ------------------------------------------------------------------ */

interface DataTableColumnBase<T> {
  key: string;
  header: React.ReactNode;
  render: (row: T) => React.ReactNode;
  sortable?: boolean;
  align?: 'left' | 'right';
  /** Extra classes for body cells (e.g. font-mono, whitespace-nowrap). */
  className?: string;
  headClassName?: string;
  /**
   * Dropped below this breakpoint regardless of the stored preference, so one
   * persisted desktop column set does not follow the user onto a phone.
   */
  hideBelow?: 'sm' | 'md';
}

/**
 * User hiding, typed so an inert flag fails `tsc` (and so `predeploy`):
 * `hideable: true` requires `label`, because `ColumnPicker` can only offer a
 * column it can name, and `defaultHidden` is allowed only with `hideable`,
 * because nothing else ever reads it. Whether the table actually uses
 * `useColumnVisibility` is a whole-file question the type cannot see —
 * lint:design and DataTable's development warning keep that one.
 */
type DataTableColumnHiding =
  | {
      /**
       * Opt IN to user hiding. A column without it is never offered by
       * `ColumnPicker` and can never be hidden, which is how the identity and
       * actions columns stay on screen by construction rather than by a flag
       * someone has to remember to set.
       */
      hideable: true;
      /**
       * Plain-text name for the column picker. `header` may be a ReactNode — the
       * actions column is `<span className="sr-only">Actions</span>` — so it can
       * never be rendered inside a menu.
       */
      label: string;
      /** Hidden until the user turns it on. */
      defaultHidden?: boolean;
    }
  | {
      hideable?: false;
      /** Naming only: it does not grant hideability. */
      label?: string;
      defaultHidden?: never;
    };

export type DataTableColumn<T> = DataTableColumnBase<T> & DataTableColumnHiding;

export interface SortState {
  column: string;
  direction: 'asc' | 'desc';
}

export interface DataTableProps<T> {
  columns: Array<DataTableColumn<T>>;
  rows: T[];
  rowKey: (row: T) => string;
  /**
   * Names the scroll region, the skip link ("Skip past {listLabel}") and the
   * batch announcements. Required: every list is a named, focusable region.
   */
  listLabel: string;
  /** Plural noun for the count strip and the trailing controls: 'sensors'. */
  itemNoun: string;
  /**
   * A plain-text card title, ONLY on a page that holds two or more tables. It
   * never carries a count — the count lives in the count strip (ruling 1).
   */
  title?: string;
  /** From the first batch; display only — it never decides whether more rows exist. */
  totalCount?: number | null;
  totalIsApproximate?: boolean;
  listState: ListStatus;
  hasMore?: boolean;
  onLoadMore?: (options?: { auto?: boolean }) => void;
  onRetryBatch?: () => void;
  onReset?: () => void;
  /** The query's raw error; the failed-batch row reads its kind to decide Retry. */
  error?: unknown;
  /** The list has a SearchInput: 'Showing 50 of 312 sensors · search to find the rest'. */
  searchable?: boolean;
  /**
   * The list's ErrorState (first load or reset failed). With no rows it takes
   * the body's place; with rows it sits above them, undimmed, and no trailing
   * control renders until the query loads (ruling 19).
   */
  errorState?: React.ReactNode;
  marks?: ReadonlyMap<string, RowMark>;
  /** Increments when a reset resolves: the region returns to the top. */
  generation?: number;
  lastRemoval?: ListRemoval | null;
  /**
   * The card header holds every query control, in a fixed order the page
   * cannot change: `filters` (ListFilter / OrganizationFilter), then `search`
   * (SearchInput), then `columnPicker` (ColumnPicker), then `toolbar`. The
   * header renders whenever any slot (or a title) is present — a list page
   * passes no title. Page verbs (Create) never go here; they live in the
   * PageHeader.
   */
  filters?: React.ReactNode;
  search?: React.ReactNode;
  columnPicker?: React.ReactNode;
  /** Trailing escape hatch for a rare per-table extra (the Sensors map toggle). Flag it in review. */
  toolbar?: React.ReactNode;
  sort?: SortState;
  onSortChange?: (sort: SortState) => void;
  /** Shown below the count strip when the list loaded and is empty. Use <EmptyState>. */
  emptyState?: React.ReactNode;
  /**
   * Opens this row's record — its one dialog (DESIGN_SYSTEM.md §6.1 Row
   * grammar). Setting it gives every row click-to-open, the hover tint and
   * cursor-pointer, and a trailing ghost Info button ("Open {name}", tooltip
   * "Open") in a pinned Actions column no column picker can hide. Gate it on
   * READ permission only; the dialog decides what is editable.
   *
   * The row itself is not a tab stop: the Info button is the keyboard path.
   * Clicks that start on an interactive descendant (button, link, form
   * control, switch, menu item, anything marked `data-row-action`) or that
   * bubble up through a React portal (menus, ConfirmDialog) never open the
   * row. Pages do not stopPropagation for this (§8.5).
   */
  onOpenRow?: (row: T) => void;
  /**
   * The record's NAME for the Info button: its accessible name becomes
   * "Open {openLabel(row)}". Required with `onOpenRow`; DataTable writes the
   * verb, so no page can word it differently.
   */
  openLabel?: (row: T) => string;
  /**
   * Task actions that are not "open" (Revoke/Restore, Manage roles, Remove,
   * Resend invitation), as `RowAction`s, rendered before Info in the pinned
   * Actions column. Never an Eye/Pencil that opens the record, never a Delete
   * when a record dialog exists, and never an attribute toggle (that stays in
   * its own column).
   */
  rowActions?: (row: T) => React.ReactNode;
  /**
   * @deprecated Use `onOpenRow` + `openLabel`. Kept one release as an alias:
   * it behaves as `onOpenRow`, and logs a development error because it renders
   * no visible open action without `openLabel`.
   */
  onRowClick?: (row: T) => void;
  /**
   * Renders a detail panel beneath a row, in a full-width cell.
   *
   * Returning null for a row means it has nothing to expand, and that row gets
   * no disclosure control at all — a chevron that opens an empty drawer is
   * worse than no chevron. Expansion is additive: a table without this prop
   * renders exactly as before, with no extra column.
   */
  renderExpanded?: (row: T) => React.ReactNode;
  /** Accessible name for the disclosure control, e.g. "versions of Foo". */
  expandLabel?: (row: T) => string;
  density?: 'default' | 'compact';
  skeletonRows?: number;
  /** Classes for the card. */
  className?: string;
}

/**
 * The props every list page passes straight from its useInfiniteList — spread
 * them, then add what only the page knows (columns, listLabel, itemNoun,
 * toolbar, emptyState, errorState).
 */
export function listTableProps<T, S extends string>(
  query: InfiniteList<T, S>,
): Pick<
  DataTableProps<T>,
  | 'rows'
  | 'totalCount'
  | 'totalIsApproximate'
  | 'listState'
  | 'hasMore'
  | 'onLoadMore'
  | 'onRetryBatch'
  | 'onReset'
  | 'error'
  | 'marks'
  | 'generation'
  | 'lastRemoval'
  | 'sort'
  | 'onSortChange'
> {
  return {
    rows: query.items,
    totalCount: query.totalCount,
    totalIsApproximate: query.totalIsApproximate,
    listState: query.status,
    hasMore: query.hasMore,
    onLoadMore: query.loadMore,
    onRetryBatch: query.retryBatch,
    onReset: query.reset,
    error: query.error,
    marks: query.marks,
    generation: query.generation,
    lastRemoval: query.lastRemoval,
    sort: query.sort,
    onSortChange: query.onSortChange,
  };
}

/** True while a retry of a failed first load or reset is running (ruling 4). */
export function isListRetrying(query: Pick<InfiniteList<unknown>, 'status' | 'error'>): boolean {
  return (query.status === 'initial' || query.status === 'refreshing') && query.error != null;
}

/** Whether the page should render its ErrorState into the errorState slot. */
export function hasListError(query: Pick<InfiniteList<unknown>, 'status' | 'error'>): boolean {
  return query.error != null && query.status !== 'appendError';
}

/**
 * The count strip's words (ruling 1). null while the first load or an
 * organization switch is running — the strip shows a Skeleton then. '' after a
 * first load that failed with nothing known — the strip is left out, because
 * '0 {itemNoun}' beside the error would be a false count.
 */
export function countSummary({
  status,
  loaded,
  total,
  approximate,
  hasMore,
  itemNoun,
  searchable,
}: {
  status: ListStatus;
  loaded: number;
  total: number | null | undefined;
  approximate?: boolean;
  hasMore?: boolean;
  itemNoun: string;
  searchable?: boolean;
}): string | null {
  if (status === 'initial') return null;
  // A failed first load knows nothing: no count at all, never a false '0'.
  if (status === 'error' && loaded === 0 && typeof total !== 'number') return '';
  if (approximate || (typeof total !== 'number' && hasMore)) {
    return `${formatNumber(loaded)}+ ${itemNoun}`;
  }
  const count = typeof total === 'number' ? total : loaded;
  if (loaded < count) {
    return `Showing ${formatNumber(loaded)} of ${formatNumber(count)} ${itemNoun}${
      searchable ? ' · search to find the rest' : ''
    }`;
  }
  return `${formatNumber(count)} ${itemNoun}`;
}

/**
 * Anything a click can start on that is its own control. A row click that
 * starts inside one of these belongs to that control, never to the row.
 */
const INTERACTIVE_SELECTOR = [
  'button',
  'a[href]',
  'input',
  'select',
  'textarea',
  'label',
  'summary',
  '[contenteditable]:not([contenteditable="false"])',
  '[role="button"]',
  '[role="link"]',
  '[role="switch"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="menuitem"]',
  '[role="menuitemcheckbox"]',
  '[role="menuitemradio"]',
  '[role="option"]',
  '[role="tab"]',
  '[role="combobox"]',
  '[data-row-action]',
].join(', ');

const FOCUSABLE_SELECTOR = 'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * True only when a click started on the row's own plain surface (cell text or
 * empty space). React bubbles portal events through the component tree, so a
 * click inside a menu or dialog rendered from a cell reaches the <tr> even
 * though the DOM target is outside it — `contains` rules those out.
 */
function isRowSurfaceClick(row: HTMLElement, target: EventTarget | null): boolean {
  if (!(target instanceof Element) || !row.contains(target)) return false;
  const control = target.closest(INTERACTIVE_SELECTOR);
  return !(control && control !== row && row.contains(control));
}

const LG_QUERY = '(min-width: 1024px)';
const ANNOUNCE_INTERVAL_MS = 2000;

/** The page h1 (PageHeader's h1 takes tabIndex -1). Where a removal that leaves no row sends focus. */
function focusListHeading(card: HTMLElement | null) {
  const main = card?.closest('main') ?? document;
  const heading = main.querySelector<HTMLElement>('h1');
  if (heading) {
    if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
    heading.focus();
  }
}

/** Search box first, then the page h1. Where a reset (not a removal) sends lost focus. */
function focusListHome(card: HTMLElement | null) {
  const main = card?.closest('main') ?? document;
  const search = main.querySelector<HTMLInputElement>('[data-list-search] input');
  if (search) {
    search.focus();
    return;
  }
  focusListHeading(card);
}

const OPEN_DIALOG_SELECTOR = '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]';
const CLOSING_DIALOG_SELECTOR = '[role="dialog"][data-state="closed"], [role="alertdialog"][data-state="closed"]';
/** How long a removal's focus request waits for closing dialogs to finish animating out. */
const REMOVAL_SETTLE_MS = 1000;

function useMediaQuery(query: string): boolean {
  const read = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
  const [matches, setMatches] = React.useState(read);
  React.useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const list = window.matchMedia(query);
    const onChange = () => setMatches(list.matches);
    onChange();
    list.addEventListener?.('change', onChange);
    return () => list.removeEventListener?.('change', onChange);
  }, [query]);
  return matches;
}

/** One polite message at a time, at most one per 2s; the latest wins. */
function useThrottledAnnouncement(): [string, (message: string) => void] {
  const [message, setMessage] = React.useState('');
  const lastAt = React.useRef(0);
  const pending = React.useRef<string | null>(null);
  const timer = React.useRef<number | null>(null);

  React.useEffect(() => () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
  }, []);

  const announce = React.useCallback((next: string) => {
    const now = Date.now();
    const wait = lastAt.current + ANNOUNCE_INTERVAL_MS - now;
    if (wait <= 0 && timer.current === null) {
      lastAt.current = now;
      setMessage(next);
      return;
    }
    pending.current = next;
    if (timer.current !== null) return;
    timer.current = window.setTimeout(() => {
      timer.current = null;
      lastAt.current = Date.now();
      if (pending.current !== null) setMessage(pending.current);
      pending.current = null;
    }, Math.max(wait, 0));
  }, []);

  return [message, announce];
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  listLabel,
  itemNoun,
  title,
  totalCount,
  totalIsApproximate = false,
  listState,
  hasMore = false,
  onLoadMore,
  onRetryBatch,
  onReset,
  error,
  searchable = false,
  errorState,
  marks,
  generation = 0,
  lastRemoval = null,
  filters,
  search,
  columnPicker,
  toolbar,
  sort,
  onSortChange,
  emptyState,
  onOpenRow,
  openLabel,
  rowActions,
  onRowClick,
  renderExpanded,
  expandLabel,
  density = 'default',
  skeletonRows = 5,
  className,
}: DataTableProps<T>): React.ReactElement {
  const id = React.useId().replace(/:/g, '');
  const cardRef = React.useRef<HTMLDivElement | null>(null);
  const regionRef = React.useRef<HTMLDivElement | null>(null);
  const bodyRef = React.useRef<HTMLTableSectionElement | null>(null);

  if (import.meta.env.DEV && (!listLabel || !itemNoun)) {
    console.error('DataTable needs listLabel and itemNoun (DESIGN_SYSTEM.md §5 Lists).');
  }

  // ---- Row grammar -----------------------------------------------------
  const open = onOpenRow ?? onRowClick;
  const hasOpenButton = Boolean(open && openLabel);
  const hasActionsColumn = Boolean(open || rowActions);
  const leadingCells = renderExpanded ? 1 : 0;

  // Development-only guards, each logged once per table.
  const warned = React.useRef<Set<string>>(new Set());
  const warnOnce = (key: string, message: string) => {
    if (warned.current.has(key)) return;
    warned.current.add(key);
    console.error(message);
  };
  if (import.meta.env.DEV) {
    if (onRowClick) {
      warnOnce(
        'onRowClick',
        'DataTable: onRowClick is deprecated — pass onOpenRow and openLabel so the row gets its visible Info "Open" button (DESIGN_SYSTEM.md §6.1 Row grammar).',
      );
    }
    if (open && !openLabel) {
      warnOnce(
        'openLabel',
        'DataTable: a clickable row needs openLabel — without it the row has no visible open action (DESIGN_SYSTEM.md §8.5).',
      );
    }
    const flagged = columns.some((column) => column.hideable || column.defaultHidden || column.hideBelow);
    if (flagged && !isVisibilityManaged(columns)) {
      warnOnce(
        'columnFlags',
        `DataTable "${listLabel}": column flags (hideable / defaultHidden / hideBelow) do nothing without useColumnVisibility — pass its columns, or delete the flags (DESIGN_SYSTEM.md §9).`,
      );
    }
  }

  // Keyed by rowKey rather than index so a re-sort does not leave a different
  // row wearing the previous one's open state.
  const [expandedKeys, setExpandedKeys] = React.useState<Set<string>>(() => new Set());
  const toggleExpanded = (key: string) =>
    setExpandedKeys((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // ---- Scroll structure ------------------------------------------------
  // At lg the page is a fixed-height column and the region is the only
  // scroller. Below lg the window scrolls and the header sticks under the app
  // bar — unless the table overflows sideways, when the region is bounded so
  // its horizontal scrollbar stays on screen.
  const isLg = useMediaQuery(LG_QUERY);
  const [overflowsX, setOverflowsX] = React.useState(false);
  const [regionWidth, setRegionWidth] = React.useState<number | null>(null);
  React.useEffect(() => {
    const region = regionRef.current;
    if (!region || typeof ResizeObserver === 'undefined') return undefined;
    let frame = 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const table = region.querySelector('table');
        setOverflowsX(table ? table.scrollWidth > region.clientWidth + 1 : false);
        setRegionWidth(region.clientWidth || null);
      });
    };
    const observer = new ResizeObserver(measure);
    observer.observe(region);
    const table = region.querySelector('table');
    if (table) observer.observe(table);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [rows.length, columns.length]);
  const bounded = isLg || overflowsX;

  // ---- States ----------------------------------------------------------
  const cellPadding = density === 'compact' ? 'px-3 py-2' : undefined;
  const loadingFirst = listState === 'initial';
  const refreshing = listState === 'refreshing';
  const hasError = Boolean(errorState);
  const showSkeleton = loadingFirst && rows.length === 0 && !hasError;
  const showErrorOnly = hasError && rows.length === 0;
  const showEmpty =
    !hasError && !loadingFirst && !refreshing && rows.length === 0 && Boolean(emptyState);
  const showTable = !showErrorOnly && !showEmpty;
  const hasControls = Boolean(filters || search || columnPicker || toolbar);
  const hasHeader = Boolean(title) || hasControls;

  // ---- Reset: back to the top, keep focus somewhere real ---------------
  const lastFocusInBody = React.useRef<Element | null>(null);
  React.useEffect(() => {
    const region = regionRef.current;
    if (!region) return undefined;
    const onFocusIn = (event: FocusEvent) => {
      const target = event.target as Element | null;
      lastFocusInBody.current =
        target && (bodyRef.current?.contains(target) || target.closest('[data-list-controls]')) ? target : null;
    };
    region.addEventListener('focusin', onFocusIn);
    return () => region.removeEventListener('focusin', onFocusIn);
  }, [showTable]);

  const previousGeneration = React.useRef(generation);
  React.useLayoutEffect(() => {
    if (previousGeneration.current === generation) return;
    previousGeneration.current = generation;
    const region = regionRef.current;
    if (region) region.scrollTop = 0;
    const focused = lastFocusInBody.current;
    if (focused && (!focused.isConnected || document.activeElement === document.body)) {
      lastFocusInBody.current = null;
      focusListHome(cardRef.current);
    }
  }, [generation]);

  // ---- Removal: focus the row that took its place ----------------------
  /*
   * A delete almost always runs from a dialog — a record and its confirmation
   * — and both animate out with focus still inside them. Radix returns focus
   * only once they unmount, to a trigger that was on the removed row, which
   * lands on <body>. So the request waits for closing dialogs to finish (up to
   * REMOVAL_SETTLE_MS), then focuses the replacing row's Info button, or the
   * page h1 when no loaded row is left. It is dropped when focus is already
   * somewhere real: inside a dialog that stayed open, or on a control outside
   * the list the user moved to.
   */
  const previousRemoval = React.useRef(lastRemoval?.token ?? 0);
  React.useEffect(() => {
    if (!lastRemoval || lastRemoval.token === previousRemoval.current) return undefined;
    previousRemoval.current = lastRemoval.token;
    const started = Date.now();
    let frame = 0;

    const focusReplacement = () => {
      const rowEl = lastRemoval.nextKey
        ? Array.from(bodyRef.current?.querySelectorAll<HTMLElement>('tr[data-row-key]') ?? []).find(
            (tr) => tr.dataset.rowKey === lastRemoval.nextKey,
          )
        : null;
      if (rowEl) {
        // The replacing row's Info button first — never a task action that
        // happens to come earlier in the row (Revoke, Manage roles).
        const target =
          rowEl.querySelector<HTMLElement>('[data-row-open] button') ??
          rowEl.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
        if (target) {
          target.focus();
          return;
        }
      }
      focusListHeading(cardRef.current);
    };

    const tick = () => {
      const active = document.activeElement;
      const connected = Boolean(active && active !== document.body && active.isConnected);
      // A dialog that is still open holds focus on purpose.
      if (connected && active?.closest(OPEN_DIALOG_SELECTOR)) return;
      const closing = document.querySelector(CLOSING_DIALOG_SELECTOR) != null;
      if (closing && Date.now() - started < REMOVAL_SETTLE_MS) {
        frame = requestAnimationFrame(tick);
        return;
      }
      const card = cardRef.current;
      const lost = !connected || Boolean(card && active && card.contains(active));
      if (lost) focusReplacement();
    };

    frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(frame);
  }, [lastRemoval]);

  // ---- Announcements ---------------------------------------------------
  const [announcement, announce] = useThrottledAnnouncement();
  const previous = React.useRef({ status: listState, count: rows.length, generation });
  React.useEffect(() => {
    const before = previous.current;
    previous.current = { status: listState, count: rows.length, generation };
    const summary = countSummary({
      status: listState,
      loaded: rows.length,
      total: totalCount,
      approximate: totalIsApproximate,
      hasMore,
      itemNoun,
    });
    if (before.generation !== generation && summary && listState !== 'initial') {
      announce(summary);
      return;
    }
    if (before.status === 'loadingMore' && listState !== 'loadingMore') {
      if (listState === 'appendError') announce(`Couldn't load more ${itemNoun}`);
      else if (listState === 'capped') announce(`Showing the first ${formatNumber(rows.length)} ${itemNoun}. Search, filter or reverse the sort to see others.`);
      else if (listState === 'stalled') announce('The list changed while loading.');
      else if (!hasMore) announce(`All ${formatNumber(rows.length)} ${itemNoun} loaded`);
      else if (summary) announce(`Loaded ${formatNumber(Math.max(0, rows.length - before.count))} more. ${summary}`);
    }
  }, [listState, rows.length, generation, hasMore, totalCount, totalIsApproximate, itemNoun, announce]);

  // ---- Sorting ---------------------------------------------------------
  const handleSort = (column: DataTableColumn<T>) => {
    if (!column.sortable || !onSortChange) return;
    const direction: SortState['direction'] =
      sort?.column === column.key && sort.direction === 'asc' ? 'desc' : 'asc';
    onSortChange({ column: column.key, direction });
  };

  const sortIcon = (column: DataTableColumn<T>) => {
    if (sort?.column !== column.key) {
      return <ArrowUpDown aria-hidden="true" className="h-3.5 w-3.5 opacity-50" />;
    }
    return sort.direction === 'asc' ? (
      <ArrowUp aria-hidden="true" className="h-3.5 w-3.5 text-primary" />
    ) : (
      <ArrowDown aria-hidden="true" className="h-3.5 w-3.5 text-primary" />
    );
  };

  const backToTop = () => {
    const region = regionRef.current;
    const behavior: ScrollBehavior = prefersReducedMotion() ? 'auto' : 'smooth';
    if (region && bounded) region.scrollTo?.({ top: 0, behavior });
    else cardRef.current?.scrollIntoView?.({ block: 'start', behavior });
    focusListHome(cardRef.current);
  };

  const summary = countSummary({
    status: listState,
    loaded: rows.length,
    total: totalCount,
    approximate: totalIsApproximate,
    hasMore,
    itemNoun,
    searchable,
  });

  const stickyHead = cn('sticky z-[1] bg-muted', bounded ? 'top-0' : 'top-16');
  const lastIndex = rows.length - 1;
  const rowCount = typeof totalCount === 'number' ? totalCount + 1 : -1;

  return (
    <>
      <a
        href={`#${id}-end`}
        className="sr-only focus:not-sr-only focus:absolute focus:z-20 focus:rounded-md focus:bg-background focus:px-3 focus:py-2 focus:text-sm focus:shadow focus:outline-none focus:ring-2 focus:ring-ring"
      >
        {`Skip past ${listLabel}`}
      </a>
      <div
        ref={cardRef}
        className={cn(
          'flex min-h-0 max-h-full flex-col overflow-clip rounded-lg border bg-card shadow-sm',
          className,
        )}
      >
        {hasHeader && (
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 p-4">
            {title ? <h2 className="text-base font-semibold">{title}</h2> : null}
            {/*
              `flex-1 min-w-0` so the controls claim the width the title leaves,
              rather than sizing to their own content; `justify-end` keeps them
              hard right; `flex-wrap` lets a narrow viewport wrap instead of
              overflowing the left edge. The order is fixed here, not by pages.
            */}
            {hasControls ? (
              <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2" data-list-toolbar="">
                {filters}
                {search}
                {columnPicker}
                {toolbar}
              </div>
            ) : null}
          </div>
        )}

        {/* The count strip: the list's only count, outside the scroll region. */}
        {summary === '' ? null : (
          <div
            className={cn(
              'shrink-0 border-b px-4 py-2 text-sm tabular-nums text-muted-foreground',
              hasHeader && 'border-t',
            )}
            data-list-count=""
          >
            {summary === null ? <Skeleton className="h-4 w-24" aria-hidden="true" /> : summary}
          </div>
        )}

        {hasError ? (
          // A table's error spans the card: no max-w-xl from ErrorState here.
          <div className="shrink-0 space-y-2 p-4 [&>[role=alert]]:max-w-none" data-list-error="">
            {errorState}
            {rows.length > 0 ? (
              <p className="text-sm text-muted-foreground">Showing results from before this change</p>
            ) : null}
          </div>
        ) : null}

        {showEmpty ? <div className="p-4">{emptyState}</div> : null}

        {showTable ? (
          <Table
            aria-rowcount={rowCount}
            aria-busy={loadingFirst || refreshing}
            wrapperRef={regionRef}
            wrapperClassName={cn(
              'relative min-h-0 w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
              bounded && 'overflow-auto',
              !isLg && overflowsX && 'max-h-[70dvh]',
            )}
            wrapperProps={{
              role: 'region',
              tabIndex: 0,
              'aria-label': listLabel,
              'data-list-region': '',
              style: regionWidth ? ({ '--region-w': `${regionWidth}px` } as React.CSSProperties) : undefined,
            }}
            trailing={
              hasError || showSkeleton ? null : (
                <ListContinuation
                  status={listState}
                  hasMore={hasMore}
                  onLoadMore={onLoadMore}
                  onRetryBatch={onRetryBatch}
                  onReset={onReset}
                  error={error}
                  itemNoun={itemNoun}
                  loadedCount={rows.length}
                  totalCount={totalCount}
                  rootRef={bounded ? regionRef : undefined}
                  onBackToTop={backToTop}
                  variant="table"
                  className="sticky left-0 w-[var(--region-w,100%)]"
                />
              )
            }
          >
            <TableHeader>
              <TableRow aria-rowindex={1}>
                {renderExpanded ? (
                  <TableHead className={cn('w-10 px-1.5', stickyHead)}>
                    <span className="sr-only">Expand</span>
                  </TableHead>
                ) : null}
                {columns.map((column) => (
                  <TableHead
                    key={column.key}
                    aria-sort={
                      sort?.column === column.key
                        ? sort.direction === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : undefined
                    }
                    className={cn(
                      stickyHead,
                      column.align === 'right' && 'text-right',
                      density === 'compact' && 'px-3',
                      column.headClassName,
                    )}
                  >
                    {column.sortable && onSortChange ? (
                      <button
                        type="button"
                        onClick={() => handleSort(column)}
                        className={cn(
                          'inline-flex items-center gap-1 rounded-sm uppercase tracking-wider transition-colors hover:text-foreground',
                          FOCUS_RING_ITEM,
                          column.align === 'right' && 'flex-row-reverse',
                        )}
                      >
                        {column.header}
                        {sortIcon(column)}
                      </button>
                    ) : (
                      column.header
                    )}
                  </TableHead>
                ))}
                {hasActionsColumn ? (
                  // Pinned: not part of `columns`, so no picker can hide it.
                  <TableHead className={cn('w-px text-right', stickyHead, density === 'compact' && 'px-3')}>
                    <span className="sr-only">Actions</span>
                  </TableHead>
                ) : null}
              </TableRow>
            </TableHeader>
            <TableBody
              ref={bodyRef}
              // Rows under a standing error stay undimmed, retry or not (ruling 19).
              className={cn('transition-opacity', refreshing && !hasError && 'opacity-50')}
            >
              {showSkeleton
                ? Array.from({ length: skeletonRows }, (_, index) => (
                    <TableRow key={`skeleton-${index}`}>
                      {renderExpanded ? <TableCell className="w-10 px-1.5" /> : null}
                      {columns.map((column) => (
                        <TableCell key={column.key} className={cn(cellPadding, column.className)}>
                          <Skeleton
                            className={cn('h-4 w-24', column.align === 'right' && 'ml-auto')}
                          />
                        </TableCell>
                      ))}
                      {hasActionsColumn ? <TableCell className={cn(cellPadding, 'w-px')} /> : null}
                    </TableRow>
                  ))
                : rows.flatMap((row, index) => {
                    const key = rowKey(row);
                    const expandedContent = renderExpanded ? renderExpanded(row) : null;
                    const isExpanded = expandedKeys.has(key);
                    const mark = marks?.get(key);
                    const isLast = index === lastIndex;
                    return [
                      <TableRow
                        key={key}
                        data-row-key={key}
                        aria-rowindex={index + 2}
                        onClick={
                          open
                            ? (event) => {
                                if (isRowSurfaceClick(event.currentTarget, event.target)) open(row);
                              }
                            : undefined
                        }
                        // React's onFocus bubbles: focusing a control in the last
                        // row (its Info button) asks for the next batch.
                        onFocus={
                          isLast && hasMore && listState === 'ready' && onLoadMore
                            ? () => onLoadMore()
                            : undefined
                        }
                        // Hover is an affordance, not decoration: only rows that
                        // open get it (ui/table's TableRow carries none).
                        className={cn(open && 'cursor-pointer hover:bg-muted/40', isExpanded && 'border-b-0')}
                      >
                        {renderExpanded ? (
                          <TableCell className="w-10 px-1.5">
                            {expandedContent ? (
                              <Button
                                variant="ghost"
                                size="icon-xs"
                                aria-expanded={isExpanded}
                                aria-controls={`row-detail-${key}`}
                                aria-label={
                                  expandLabel
                                    ? `${isExpanded ? 'Hide' : 'Show'} ${expandLabel(row)}`
                                    : isExpanded ? 'Hide details' : 'Show details'
                                }
                                onClick={(event) => {
                                  // The row-click guard already ignores clicks that
                                  // start on a button; this is belt and braces.
                                  event.stopPropagation();
                                  toggleExpanded(key);
                                }}
                              >
                                <DisclosureChevron open={isExpanded} />
                              </Button>
                            ) : null}
                          </TableCell>
                        ) : null}
                        {columns.map((column, columnIndex) => (
                          <TableCell
                            key={column.key}
                            className={cn(
                              cellPadding,
                              column.align === 'right' && 'text-right tabular-nums',
                              column.className,
                            )}
                          >
                            {column.render(row)}
                            {mark && columnIndex === 0 ? (
                              <StatusBadge status={mark} className="ml-2 align-middle" />
                            ) : null}
                          </TableCell>
                        ))}
                        {hasActionsColumn ? (
                          <TableCell className={cn(cellPadding, 'w-px whitespace-nowrap text-right')}>
                            <div className="flex items-center justify-end gap-1">
                              {rowActions?.(row)}
                              {hasOpenButton && open && openLabel ? (
                                <span className="contents" data-row-open="">
                                  <RowAction
                                    icon={Info}
                                    label="Open"
                                    subject={openLabel(row)}
                                    onClick={() => open(row)}
                                  />
                                </span>
                              ) : null}
                            </div>
                          </TableCell>
                        ) : null}
                      </TableRow>,
                      // The detail row is only mounted while open. It carries no
                      // aria-rowindex: it is not one of the list's rows.
                      isExpanded && expandedContent ? (
                        <TableRow key={`${key}-detail`}>
                          <TableCell
                            id={`row-detail-${key}`}
                            colSpan={columns.length + leadingCells + (hasActionsColumn ? 1 : 0)}
                            className="bg-muted/30 px-4 py-3"
                          >
                            {expandedContent}
                          </TableCell>
                        </TableRow>
                      ) : null,
                    ];
                  })}
            </TableBody>
          </Table>
        ) : null}

        <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
          {announcement}
        </div>
      </div>
      <span id={`${id}-end`} tabIndex={-1} className="sr-only" />
    </>
  );
}
